import React from "react";
import SearchInput from "./SearchInput";

export interface DropdownOption {
  value: string;
  label: string;
  hint?: string;
}

interface CustomDropdownProps {
  options: DropdownOption[];
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  loading?: boolean;
  disabled?: boolean;
  emptyMessage?: string;
  className?: string;
  searchMinChars?: number;
  showSearch?: boolean;
  buttonClass?: string;
}

export function CustomDropdown({
  options,
  value,
  onChange,
  placeholder,
  loading,
  disabled,
  emptyMessage = "No results",
  className,
  searchMinChars = 3,
  showSearch = true,
  buttonClass,
}: CustomDropdownProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  const selected = React.useMemo(() => options.find((o) => o.value === value) || null, [options, value]);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length >= searchMinChars) {
      return options.filter((o) =>
        o.label.toLowerCase().includes(q) || (o.hint ?? "").toLowerCase().includes(q)
      );
    }
    return options;
  }, [options, query, searchMinChars]);

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={containerRef} className={`relative ${className ?? ""}`}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        className={`h-8 px-2 text-black text-xs !border !border-solid rounded w-56 flex items-center justify-between ${disabled ? "bg-gray-100 !border-gray-200 text-gray-400" : "bg-white !border-gray-300 hover:!border-gray-400"
          } ${buttonClass ?? ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="truncate text-left">

          {selected ? (
            <>
              {selected.label}
              {selected.hint ? <span className="text-gray-400"> ({selected.hint})</span> : null}
            </>
          ) : (
            <span className="text-gray-400">{placeholder}</span>
          )}
        </span>
        <span className="ml-2 text-gray-400">▾</span>
      </button>

      {open && (
        <div className="flex flex-col max-h-64 absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded shadow-lg p-2">
          {loading ? (
            <div className="p-2">
              <div className="h-7 w-full bg-gray-200 rounded animate-pulse mb-2" />
              <div className="space-y-2">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-6 w-full bg-gray-100 rounded animate-pulse" />
                ))}
              </div>
            </div>
          ) : (
            <>
              {showSearch && (
                <SearchInput
                  value={query}
                  onChange={setQuery}
                  placeholder="Search"
                  minChars={searchMinChars}
                  className="mb-2"
                />
              )}
              <ul role="listbox" className="flex-auto overflow-auto divide-y divide-gray-50">
                {filtered.length === 0 ? (
                  <li className="py-2 px-2 text-xs text-gray-500">{emptyMessage}</li>
                ) : (
                  filtered.map((opt) => (
                    <li key={opt.value}>
                      <button
                        type="button"
                        className={`w-full justify-start py-2 px-2 text-xs hover:bg-gray-50 ${value === opt.value ? "bg-primary-light" : ""
                          }`}
                        onClick={() => {
                          onChange(opt.value);
                          setOpen(false);
                        }}
                        role="option"
                        aria-selected={value === opt.value}
                      >
                        <div className="truncate">
                          <span className="font-medium text-gray-900">{opt.label}</span>
                          {opt.hint ? <span className="text-gray-400"> ({opt.hint})</span> : null}
                        </div>
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default CustomDropdown;
